import React, { useState } from 'react';
import { Button, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { GmailIcon } from '../../../design-system';
import axios from 'axios';
import { userService } from '../../../services/userService';
import { popupOAuth, oauthEvents } from './popupOAuth';

// API Configuration
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

// Auto-select API by environment
const API_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host || '';
    const hostname = window.location.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com') || host.includes('adly-app-dev');
    if (isLocalhost || isDevSite) return DEV_API_BASE;
  }
  return PROD_API_BASE;
})();

interface GmailOAuthButtonProps {
  onSuccess?: () => void;
  style?: React.CSSProperties;
  ownershipType?: 'personal' | 'shared';
}

interface GmailAuthResponse {
  auth_url: string;
  message?: string;
}

const GmailOAuthButton: React.FC<GmailOAuthButtonProps> = ({ onSuccess, style, ownershipType = 'personal' }) => {
  const [loading, setLoading] = useState(false);

  const handleGmailOAuth = async () => {
    setLoading(true);
    
    try {
      // Get authentication credentials
      const token = await userService.getAuthToken();
      
      console.log('[GmailOAuth] Starting popup OAuth flow...');
      
      // Record when this OAuth session started
      const sessionStartTime = new Date().toISOString();
      
      // Get the OAuth URL from the backend
      const response = await axios.post<GmailAuthResponse>(
        `${API_BASE_URL}/gmail/gmail-auth-process`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[GmailOAuth] Auth URL received:', response.data.auth_url);
      
      // Create a function that gets fresh headers for each poll
      const getFreshHeaders = async () => {
        try {
          const freshToken = await userService.getAuthToken();
          return {
            'Authorization': `Bearer ${freshToken}`,
            'Content-Type': 'application/json'
          };
        } catch (error) {
          console.error('[GmailOAuth] Error getting fresh headers:', error);
          return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };
        }
      };
      
      // Use popup OAuth instead of redirect with session_start parameter
      const result = await popupOAuth(
        response.data.auth_url,
        `${API_BASE_URL}/gmail/gmail-auth-status?session_start=${encodeURIComponent(sessionStartTime)}`,
        getFreshHeaders,
        {
          popupTitle: 'Connect Gmail',
          width: 600,
          height: 700,
          pollInterval: 3000, // Check every 3 seconds
          maxAttempts: 30 // 90 seconds total
        }
      );
      
      if (result.success) {
        console.log('✅ Gmail channel connected:', result.data);
        
        // Emit global event for other components
        oauthEvents.emit('channelConnected', {
          type: 'gmail',
          data: result.data
        });
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(result.error || 'Authentication failed');
      }
      
    } catch (error: any) {
      console.error('[GmailOAuth] Error in popup OAuth:', error);
      
      // Extract error message
      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error occurred';
      
      // message.error(`Failed to connect Gmail: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        icon={loading ? <LoadingOutlined /> : <GmailIcon size={16} />}
        onClick={handleGmailOAuth}
        loading={loading}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
          color: '#374151',
          borderRadius: '6px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: 500,
          transition: 'all 0.2s ease',
          ...style
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#EA4335';
          e.currentTarget.style.color = '#EA4335';
          e.currentTarget.style.backgroundColor = '#f8fafc';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#e5e7eb';
          e.currentTarget.style.color = '#374151';
          e.currentTarget.style.backgroundColor = '#fff';
        }}
      >
        {loading ? 'Connecting...' : 'Gmail'}
      </Button>
    </>
  );
};

export default GmailOAuthButton;

